import { useTranslation } from '@thoughtspot/i18n';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { getCurrentOrgId, isOrgUiEnabled } from '@thoughtspot/session-service';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState, } from 'react';
import { useGetStatus } from '../../../services/sidebar/sidebar-service';
import { getLegacyUserManagementVisibility } from '../../../utils/sidebar-config';
import { AdminPageHeader } from '../admin-page-header/admin-page-header';
import { UserManagementTranslationKeys } from '../constants';
import UsersContainer from './users/users.container';
const GroupsContainer = lazy(() => import('./groups/groups.container'));
const RolesContainer = lazy(() => import('./roles/roles.container'));
const AuthenticationContainer = lazy(() => import('./authentication/authentication.container'));
export var UserManagementTab;
(function (UserManagementTab) {
    UserManagementTab["Users"] = "users";
    UserManagementTab["Groups"] = "groups";
    UserManagementTab["Roles"] = "roles";
    UserManagementTab["Authentication"] = "authentication";
})(UserManagementTab || (UserManagementTab = {}));
const TAB_COMPONENTS = {
    [UserManagementTab.Users]: UsersContainer,
    [UserManagementTab.Groups]: GroupsContainer,
    [UserManagementTab.Roles]: RolesContainer,
    [UserManagementTab.Authentication]: AuthenticationContainer,
};
export const UserManagementContainer = ({ currentOrgId, }) => {
    const effectiveCurrentOrgId = currentOrgId ?? getCurrentOrgId();
    const isAllOrgContext = effectiveCurrentOrgId === -1;
    const viewMode = useMemo(() => {
        if (!isOrgUiEnabled()) {
            return '';
        }
        if (effectiveCurrentOrgId === -1) {
            return 'all';
        }
        if (effectiveCurrentOrgId === 0) {
            return 'primary';
        }
        return '';
    }, [effectiveCurrentOrgId]);
    const orgsEnabledForStatus = useMemo(() => {
        if (!isOrgUiEnabled()) {
            return false;
        }
        return !isAllOrgContext;
    }, [isAllOrgContext]);
    const { data: statusData, loading: isStatusLoading } = useGetStatus(viewMode, orgsEnabledForStatus);
    const statusConfig = statusData?.Data;
    const visibility = useMemo(() => statusConfig
        ? getLegacyUserManagementVisibility(statusConfig, isAllOrgContext)
        : null, [isAllOrgContext, statusConfig]);
    const [currentTab, setCurrentTab] = useState(UserManagementTab.Users);
    const handleTabChange = useCallback((tab) => {
        if (tab in TAB_COMPONENTS) {
            setCurrentTab(tab);
        }
    }, []);
    const { t } = useTranslation();
    const ActiveTabComponent = TAB_COMPONENTS[currentTab];
    const tabs = useMemo(() => [
        {
            id: UserManagementTab.Users,
            title: t(UserManagementTranslationKeys.TAB_USERS),
            trackId: 'user-management-users',
            show: visibility?.users ?? false,
        },
        {
            id: UserManagementTab.Groups,
            title: t(UserManagementTranslationKeys.TAB_GROUPS),
            trackId: 'user-management-groups',
            show: visibility?.groups ?? false,
        },
        {
            id: UserManagementTab.Roles,
            title: t(UserManagementTranslationKeys.TAB_ROLES),
            trackId: 'user-management-roles',
            show: visibility?.roles ?? false,
        },
        {
            id: UserManagementTab.Authentication,
            title: t(UserManagementTranslationKeys.TAB_AUTHENTICATION),
            trackId: 'user-management-authentication',
            show: visibility?.authentication ?? false,
        },
    ], [t, visibility]);
    const visibleTabs = useMemo(() => tabs.filter(tab => tab.show !== false), [
        tabs,
    ]);
    useEffect(() => {
        if (visibleTabs.length > 0 &&
            !visibleTabs.some(tab => tab.id === currentTab)) {
            setCurrentTab(visibleTabs[0].id);
        }
    }, [currentTab, visibleTabs]);
    return (React.createElement(Vertical, null,
        React.createElement(AdminPageHeader, { title: t(UserManagementTranslationKeys.TITLE), tabs: tabs, selectedTabId: currentTab, onTabChange: id => handleTabChange(id), titleTestId: "user-management-title", tabTestIdPrefix: "user-management-tab" }),
        React.createElement(Suspense, { fallback: React.createElement(LoadingIndicator.Global, null,
                React.createElement(React.Fragment, null)) }, !visibility && isStatusLoading ? (React.createElement(LoadingIndicator.Global, null,
            React.createElement(React.Fragment, null))) : (React.createElement(ActiveTabComponent, { key: effectiveCurrentOrgId, currentOrgId: effectiveCurrentOrgId })))));
};
//# sourceMappingURL=user-management.container.js.map